import { Key } from "react";
import {
  CONTENT_HEALTH_STATUS_TAGS,
  ContentHealthStatus,
} from "../../../constants/status-colors";
import {
  ContentNodeStatus,
  ContentNodeType,
  TreeNode,
} from "../../../services/content-tree/content-tree.types";

// Short, self-explanatory abbreviations shown as a Tag in front of each
// node's title. Node types the UI doesn't know (the backend response isn't
// typed at runtime) fall back to the uppercased raw type in NodeTitle.
export const TYPE_ABBR: Record<ContentNodeType, string> = {
  program: "PROG",
  course: "CURSO",
  lesson: "LEC",
  activity: "ACT",
  resource: "REC",
  project: "PROY",
  phase: "FASE",
  game_quiz: "GQ",
};

// Non-active statuses share color and copy with the Dashboard via
// status-colors.ts; the map shows the singular per-node label ("Eliminado")
// instead of the dashboard's plural count label ("Eliminados").
const healthTag = (status: ContentHealthStatus) => ({
  color: CONTENT_HEALTH_STATUS_TAGS[status].color,
  label: CONTENT_HEALTH_STATUS_TAGS[status].mapLabel,
});

export const STATUS_TAG: Record<
  ContentNodeStatus,
  { color: string; label: string }
> = {
  active: { color: "green", label: "Activo" },
  deleted: healthTag("deleted"),
  detached: healthTag("detached"),
  broken: healthTag("broken"),
  incomplete: healthTag("incomplete"),
};

type NonActiveStatus = Exclude<ContentNodeStatus, "active">;

interface TypeLabel {
  singular: string;
  plural: string;
  gender: "m" | "f";
}

// Gendered labels so the status summary reads grammatically correct in
// Spanish (e.g. "3 cursos incompletos" vs "2 lecciones incompletas").
// Exported so the attention queue reuses the same nouns for its badges.
export const NODE_TYPE_LABEL: Record<ContentNodeType, TypeLabel> = {
  program: { singular: "programa", plural: "programas", gender: "m" },
  course: { singular: "curso", plural: "cursos", gender: "m" },
  lesson: { singular: "lección", plural: "lecciones", gender: "f" },
  activity: { singular: "actividad", plural: "actividades", gender: "f" },
  resource: { singular: "recurso", plural: "recursos", gender: "m" },
  project: { singular: "proyecto", plural: "proyectos", gender: "m" },
  phase: { singular: "fase", plural: "fases", gender: "f" },
  game_quiz: {
    singular: "quiz de juego",
    plural: "quizzes de juego",
    gender: "m",
  },
};

interface StatusAdjective {
  m: string;
  f: string;
  mPlural: string;
  fPlural: string;
}

const STATUS_ADJECTIVE: Record<NonActiveStatus, StatusAdjective> = {
  deleted: {
    m: "eliminado",
    f: "eliminada",
    mPlural: "eliminados",
    fPlural: "eliminadas",
  },
  detached: {
    m: "desvinculado",
    f: "desvinculada",
    mPlural: "desvinculados",
    fPlural: "desvinculadas",
  },
  broken: { m: "roto", f: "rota", mPlural: "rotos", fPlural: "rotas" },
  incomplete: {
    m: "incompleto",
    f: "incompleta",
    mPlural: "incompletos",
    fPlural: "incompletas",
  },
};

export const describeStatusCount = (
  type: ContentNodeType,
  status: NonActiveStatus,
  count: number
): string => {
  // The backend response isn't typed at runtime, so a node type this UI
  // version doesn't know falls back to the raw type as its noun.
  const typeLabel: TypeLabel = NODE_TYPE_LABEL[type] ?? {
    singular: type,
    plural: type,
    gender: "m",
  };
  const adjective = STATUS_ADJECTIVE[status];
  if (count === 1) {
    const adj = typeLabel.gender === "f" ? adjective.f : adjective.m;
    return `1 ${typeLabel.singular} ${adj}`;
  }
  const adj = typeLabel.gender === "f" ? adjective.fPlural : adjective.mPlural;
  return `${count} ${typeLabel.plural} ${adj}`;
};

export interface StatusSummaryEntry {
  type: ContentNodeType;
  status: NonActiveStatus;
  count: number;
}

/** Walks the tree client-side and counts nodes per (type, status) pair for
 * every non-active status — used for the "3 cursos incompletos, ..." header. */
export const computeStatusSummary = (root: TreeNode): StatusSummaryEntry[] => {
  const counts = new Map<string, StatusSummaryEntry>();

  const walk = (node: TreeNode) => {
    if (node.status !== "active") {
      const status = node.status as NonActiveStatus;
      const key = `${node.type}:${status}`;
      const existing = counts.get(key);
      if (existing) existing.count += 1;
      else counts.set(key, { type: node.type, status, count: 1 });
    }
    (node.children ?? []).forEach(walk);
  };

  walk(root);
  return Array.from(counts.values());
};

/** Expands the program and course levels by default; deeper levels
 * (lesson/activity/resource/etc.) stay collapsed until the user expands
 * them or a search reveals them. */
export const computeDefaultExpandedKeys = (root: TreeNode): Key[] => {
  const keys: Key[] = [];

  const walk = (node: TreeNode) => {
    if (node.type === "program" || node.type === "course") {
      keys.push(node.key);
      (node.children ?? []).forEach(walk);
    }
  };

  walk(root);
  return keys;
};

// Per-character diacritic stripping keeps the normalized string the same
// length as the original, so a match index found in the normalized string
// maps directly onto the original (accented) string for highlighting.
export const normalizeForSearch = (text: string): string =>
  text
    .split("")
    .map((char) => char.normalize("NFD").replace(/[\u0300-\u036f]/g, ""))
    .join("")
    .toLowerCase();

export const nodeMatchesSearch = (node: TreeNode, term: string): boolean => {
  const trimmed = term.trim();
  if (!trimmed) return false;
  if (String(node.id).includes(trimmed)) return true;
  return normalizeForSearch(node.title).includes(normalizeForSearch(trimmed));
};

/** Returns the [start, end) range of the first match of `term` inside
 * `title`, comparing case- and diacritic-insensitively but returning
 * indices valid against the original (unmodified) `title` string. */
export const findHighlightRange = (
  title: string,
  term: string
): [number, number] | null => {
  const trimmed = term.trim();
  if (!trimmed) return null;
  const needle = normalizeForSearch(trimmed);
  const idx = normalizeForSearch(title).indexOf(needle);
  if (idx === -1) return null;
  return [idx, idx + needle.length];
};

/** Walks the tree and returns the keys of every node that has at least one
 * matching descendant — i.e. all ancestors of matches, so the Tree can be
 * auto-expanded down to reveal them. The matched node itself is not
 * included (only its ancestors need to expand). */
export const collectExpandedKeysForMatches = (
  root: TreeNode,
  term: string
): Key[] => {
  const keys: Key[] = [];

  const walk = (node: TreeNode): boolean => {
    const isMatch = nodeMatchesSearch(node, term);
    const childMatched = (node.children ?? [])
      .map(walk)
      .some((matched) => matched);
    if (childMatched) keys.push(node.key);
    return isMatch || childMatched;
  };

  walk(root);
  return keys;
};

const EDIT_PATH_BY_TYPE: Partial<Record<ContentNodeType, string>> = {
  course: "/courses",
  lesson: "/lessons",
  resource: "/resources",
  program: "/programs",
};

/** Returns the edit route for a node, or undefined for types that don't have
 * a standalone edit page (activity/phase/project/game_quiz). Takes only
 * type+id so flat rows (attention queue) can reuse it besides tree nodes. */
export const getEditPath = (
  node: Pick<TreeNode, "type" | "id">
): string | undefined => {
  const base = EDIT_PATH_BY_TYPE[node.type];
  return base ? `${base}/${node.id}` : undefined;
};
